import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { upperCase, lowerCase, isEmpty } from 'lodash';

const SwitchOnboardingLanguage = ({ 
  currentLanguage = 'en', 
  onLanguageChange,
  supportedLocales = ['en', 'fr'],
  updateUserData
}) => {
  // If no supported locales or only one, don't render
  if (isEmpty(supportedLocales) || supportedLocales.length < 2) return null;
  
  const handleLanguageChange = (e) => {
    const newLanguage = lowerCase(e.target.value);
    console.log(`Changing language from ${currentLanguage} to ${newLanguage}`);
    
    // Update the language in localStorage for persistence
    localStorage.setItem('findbob_language', newLanguage);
    
    // Update the user's locale if needed
    if (updateUserData) {
      updateUserData({ locale: newLanguage });
    }
    
    // Call the onLanguageChange handler if provided
    if (onLanguageChange) {
      onLanguageChange(newLanguage);
    }
    
    // Update logo if needed (keeping this from SwitchLocale)
    if (newLanguage === 'fr') {
      const logo = document.querySelector('.logo img');
      if (logo) logo.src = logo.src.replace('.png', '_FR.png');
    } else {
      const logo = document.querySelector('.logo img');
      if (logo) logo.src = logo.src.replace('_FR', '');
    }
    
    // Update Intercom settings if Intercom exists
    if (window.Intercom) {
      window.intercomSettings = {
        ...window.intercomSettings,
        language_override: newLanguage,
      };
      window.Intercom('boot', window.intercomSettings);
    }
    
    // Reload the page to apply the language change
    // This is the simplest way to ensure all components get updated
    window.location.reload();
  };

  // Get translations for accessibility
  const getTranslation = (key) => {
    const translations = {
      en: {
        'select.language': 'Select language',
        'english': 'English',
        'french': 'French',
        'english_uk': 'English (UK)'
      },
      fr: {
        'select.language': 'Sélectionner la langue',
        'english': 'Anglais',
        'french': 'Français',
        'english_uk': 'Anglais (UK)'
      },
      'en-GB': {
        'select.language': 'Select language',
        'english': 'English',
        'french': 'French',
        'english_uk': 'English (UK)'
      }
    };
    
    return translations[currentLanguage]?.[key] || translations['en'][key] || key;
  };
  
  // Get language names for screen readers
  const getLanguageName = (locale) => {
    const languageNames = {
      'en': getTranslation('english'),
      'fr': getTranslation('french'),
      'en-GB': getTranslation('english_uk')
    };
    
    return languageNames[locale.toLowerCase()] || locale.toUpperCase();
  };

  return (
    <select
      value={upperCase(currentLanguage)}
      onChange={handleLanguageChange}
      className="px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
      aria-label={getTranslation('select.language')}
    >
      {supportedLocales.map(locale => (
        <option key={locale} value={upperCase(locale)} title={getLanguageName(locale)}>
          {upperCase(locale)}
        </option>
      ))}
    </select>
  );
};

SwitchOnboardingLanguage.propTypes = {
  currentLanguage: PropTypes.string,
  onLanguageChange: PropTypes.func,
  supportedLocales: PropTypes.arrayOf(PropTypes.string),
  updateUserData: PropTypes.func
};

export default SwitchOnboardingLanguage; 